import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  CreateGroupDocument,
  type CreateGroupMutation,
  type CreateGroupMutationVariables,
  GetGroupsDocument,
} from "~/features/admin/groups/gqls";
import type { GroupFormData } from "~/features/admin/groups/types";
import { translateError } from "~/lib/errors";

interface UseGroupCreateOptions {
  onSuccess?: (data: GroupFormData) => void;
}

export function useGroupCreate({ onSuccess }: UseGroupCreateOptions = {}) {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const [createGroup] = useMutation<CreateGroupMutation, CreateGroupMutationVariables>(CreateGroupDocument, {
    refetchQueries: [GetGroupsDocument],
  });

  const submit = useCallback(
    async (formData: GroupFormData) => {
      setProcessing(true);
      try {
        await createGroup({
          variables: {
            input: {
              name: formData.name,
              description: formData.description,
            },
          },
        });
        toast.success(t("feature.common.createdSuccessfully", { model: "group" }));
        onSuccess?.(formData);
      } catch (e) {
        toast.error(translateError(t, e));
      } finally {
        setProcessing(false);
      }
    },
    [createGroup, t, onSuccess],
  );

  return { submit, processing };
}
